<template>
  <div class="relative" ref="root">
    <label v-if="label" class="mb-2 block text-sm font-medium text-gray-700">
      {{ label }}
    </label>

    <button
      type="button"
      @click="toggle"
      class="flex h-11 w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
      data-testid="custom_select"
    >
      <span :class="modelValue ? 'text-gray-900' : 'text-gray-400'">
        {{ modelValue || placeholder }}
      </span>

      <ChevronUp v-if="open" class="h-[15px] w-[20px] text-gary-light-200" />
      <ChevronDown v-else class="h-[15px] w-[20px] text-gray-light-200" />
    </button>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <ul v-if="open" class="absolute z-10 mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-lg">
        <li
          v-for="(item, index) in items"
          :key="index"
          @click="select(item as string)"
          class="flex cursor-pointer items-center px-4 py-3 text-sm hover:bg-gray-50"
          data-testid="custom_select_options"
        >
          <div class="w-5">
            <Check class="h-4 w-4 text-primary-600" v-if="item === modelValue" />
          </div>
          <span class="text-gray-900">
            {{ item }}
          </span>
        </li>
      </ul>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { Check, ChevronDown, ChevronUp } from 'lucide-vue-next';
import { onBeforeUnmount, onMounted, ref } from 'vue';

defineProps({
  label: String,
  items: {
    type: Array,
    required: true,
  },
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: 'Select an option',
  },
});

const emit = defineEmits(['update:modelValue']);

const open = ref(false);
const root = ref<HTMLElement | null>(null);

const onDocClick = (e: Event) => {
  const target = e.target as Node | null;
  if (!root.value) return;
  if (!target) return;
  if (!root.value.contains(target)) {
    open.value = false;
  }
};

onMounted(() => document.addEventListener('click', onDocClick));
onBeforeUnmount(() => document.removeEventListener('click', onDocClick));

const toggle = () => {
  open.value = !open.value;
};

function select(value: String | Number) {
  emit('update:modelValue', value);
  open.value = false;
}
</script>
